import { useQuery } from "@apollo/client";
import { GarageDaysQuery, GET_GARAGE_DAYS } from "@App/graphql/queries";
import { parseISO } from "date-fns";
import { useQueryPollingWhileWindowFocused } from "@App/hooks/api/useQueryPollingWhileWindowFocused";
import { useCalculation } from "@App/hooks/useCalculation";
import { useContext } from "@App/hooks/useContext";
import { formatDate, toIsoString } from "@App/utils/dates";

export const useDays = (from: Date, to: Date) => {
  const {
    organization,
    service,
    garage,
    filterBrand,
    brand,
    generation,
    motorization,
    body,
    gearbox,
    mileage,
    initialRegistrationDate,
    subRepairs,
    equipments,
    premiumServices,
  } = useCalculation();

  const { getContext } = useContext();

  const { loading, data, error, refetch, startPolling, stopPolling, networkStatus } =
    useQuery<GarageDaysQuery>(GET_GARAGE_DAYS, {
      variables: {
        organization: organization ? organization.id : null,
        garage: garage ? garage.id : null,
        services: service ? [service.id] : null,
        startDate: toIsoString(from),
        endDate: toIsoString(to),
        brand: brand ? brand.id : filterBrand ? filterBrand.id : null,
        generation: generation ? generation.id : null,
        motorization: motorization ? motorization.id : null,
        body: body ? body.id : null,
        gearbox: gearbox ? gearbox.id : null,
        mileage: mileage,
        registrationDate: initialRegistrationDate
          ? formatDate(initialRegistrationDate, "yyyy-MM-dd")
          : null,
        premiumServices:
          premiumServices && premiumServices.length > 0 ? premiumServices.map((p) => p.id) : null,
        equipments: equipments.map((equipment) => equipment.name),
        subRepairs: subRepairs,
      },
      skip: !organization || !garage || !service || !from || !to,
      context: getContext(garage),
    });

  // only poll for new days if window is focused
  useQueryPollingWhileWindowFocused({
    pollInterval: 300000,
    loading,
    refetch: refetch,
    skip: !organization || !garage || !service || !from || !to,
    startPolling: startPolling,
    stopPolling: stopPolling,
  });

  return {
    loading,
    error,
    networkStatus,
    days: data && data.garage.days ? data.garage.days.map((date) => parseISO(date)) : [],
    nearestDay: data && data.garage.days ? data.garage.days[0] : null,
  };
};
